/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InputType } from '../nos-business-sign-up';
import { INosBusinessSignUpResources } from '../nos-business-sign-up.props.autogenerated';
import SignUpButton from './sign-up-button';

export interface IBusinessSignUpInputProps {
    id?: string;
    type: InputType;
    value: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    isRequired: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    onChange?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onClick?: (id: string) => void;
    telemetryContent: ITelemetryContent;
    buttonClassName?: string;
    isContinueWithoutVatId?: boolean;
    isVatValidationClicked?: boolean;
    isVatCheckFirstClickDone?: boolean;
    handleIscontinueWithoutVatId?: () => void;
    isVatValid?: boolean;
    resources: INosBusinessSignUpResources;
    isVatCheckExcecuted?: boolean;
    isSubmittedPressed: boolean;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }

    return undefined;
};

export const SignUpVAT: React.FC<IBusinessSignUpInputProps> = ({
    id,
    resources,
    type,
    value,
    pattern,
    className,
    maxLength,
    isRequired,
    onChange,
    fieldReference,
    onClick,
    telemetryContent,
    buttonClassName,
    handleIscontinueWithoutVatId,
    isContinueWithoutVatId,
    isVatValidationClicked,
    isVatCheckFirstClickDone,
    isVatValid,
    isVatCheckExcecuted,
    isSubmittedPressed
}) => {
    return (
        <React.Fragment>
            {handleIscontinueWithoutVatId && (
                <div className='ms-form__group-horizontal mb-m'>
                    <div className='col-12'>
                        <input
                            className='ms-form__input-checkbox'
                            type='checkbox'
                            id='check1'
                            name='option1'
                            checked={isContinueWithoutVatId}
                            onChange={() => handleIscontinueWithoutVatId()}
                        />
                        <label className='ms-form__label-checkbox' htmlFor='check1'>
                            {resources.withoutVatCheckbox}
                        </label>
                    </div>
                </div>
            )}
            <p>{resources.vatLabel}</p>
            {!isVatValid && !isVatValidationClicked && !isContinueWithoutVatId && (
                <label className='ms-form__label'>{resources.continueWithoutVatOrValidate}</label>
            )}
            <div className='ms-form__group-horizontal'>
                {!isContinueWithoutVatId && (
                    <div className='col-12 col-md-6'>
                        <input
                            id={id}
                            type={type}
                            value={value}
                            className={`${className}-input ${className}-${id} ms-form__input ${value === '' &&
                                isSubmittedPressed &&
                                'necessary'}`}
                            pattern={pattern !== '' ? pattern : undefined}
                            aria-required={isRequired}
                            maxLength={GetMaxLength(maxLength)}
                            onChange={onChange}
                            ref={fieldReference}
                            disabled={isContinueWithoutVatId}
                        />
                    </div>
                )}
                {onClick ? (
                    <>
                        {!isContinueWithoutVatId && (
                            <div className='col-12 col-md-6'>
                                <SignUpButton
                                    {...{
                                        id,
                                        value,
                                        className: buttonClassName ? `${buttonClassName} ms-button__primary-dark` : '',
                                        ariaLabel: 'label',
                                        telemetryContent: telemetryContent,
                                        text: isVatCheckFirstClickDone ? resources.vatRevalidateButton : resources.vatValidateButton,
                                        onClick: () => onClick('123'),
                                        disabled: isContinueWithoutVatId
                                    }}
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </div>
            {/* System currently unavailable */}
            {!isContinueWithoutVatId && !isVatCheckExcecuted && isVatValidationClicked && (
                <div className='ms-alert ms-alert-warning mt-m'>{resources.vatIdFailed}</div>
            )}
            {/* VAT ID valid */}
            {isVatValid && !isContinueWithoutVatId && <div className='ms-alert ms-alert-success mt-m'>{resources.vatIdValid}</div>}
            {/* VAT ID valid but "without VAT id" is active */}
            {isVatValid && isContinueWithoutVatId && (
                <div className='ms-alert ms-alert-warning mt-m'>{resources.uncheckToContinueWithValidVat}</div>
            )}
            {/* VAT ID invalid */}
            {isVatCheckExcecuted && isVatValidationClicked && !isVatValid && !isContinueWithoutVatId && (
                <div className='ms-alert ms-alert-danger mt-m' dangerouslySetInnerHTML={{ __html: resources.vatIdNotValid }} />
            )}
            {(isContinueWithoutVatId || !isVatValid) && <p className='mt-m'>{resources.youWillBeChargedVat} </p>}
        </React.Fragment>
    );
};

export default SignUpVAT;
